"use client";

import {
  FolderDot,
  LifeBuoy,
} from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";

import { Button } from "~/components/ui/button";
import { Separator } from "~/components/ui/separator";
import { useBrand } from "~/config/brands";
import { resetHireBuilderForNewAgent } from "~/lib/marketplace/hire-onboarding-session";
import { getMarketingChrome } from "~/lib/siteglue/marketing-chrome";
import { clearOnboardSession } from "~/lib/siteglue/onboard-session";
import {
  BUILD_NEW_INBOUND_AGENT_PATH,
  BUILD_NEW_SITEGLUE_AGENT_PATH,
  canBuildAndPurchaseAgents,
  cn,
} from "~/lib/utils";
import { Nav, type NavProps } from "./nav";

type SidebarProject = {
  id: string;
  title?: string | null;
  faviconUrl?: string | null;
};

export function AppShellSidebar({
  isCollapsed,
  chrome,
  isSiteGlue,
  brand,
  projects,
  isSidebarAgentsLoading,
  orgRole,
  additionalLinks,
  onNavigate,
  onSupport,
}: {
  isCollapsed: boolean;
  chrome: ReturnType<typeof getMarketingChrome>;
  isSiteGlue: boolean;
  brand: ReturnType<typeof useBrand>;
  projects: SidebarProject[] | undefined;
  isSidebarAgentsLoading: boolean;
  orgRole: string | null | undefined;
  additionalLinks: NavProps["links"];
  onNavigate?: () => void;
  onSupport: () => void;
}) {
  const router = useRouter();

  const links = additionalLinks.map((link) => ({
    ...link,
    onClick: () => {
      link.onClick?.();
      onNavigate?.();
    },
  }));

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col">
      <div
        className={cn(
          "flex shrink-0 items-center border-b",
          isCollapsed ? "justify-center px-2 py-3" : "gap-2 px-3 py-3",
        )}
      >
        <Link
          href="/"
          onClick={onNavigate}
          className={cn(
            "flex min-w-0 items-center gap-2",
            isCollapsed && "justify-center",
          )}
        >
          <Image
            src={chrome.logoSrc}
            alt={chrome.logoAlt}
            width={32}
            height={32}
            className="h-8 w-8 shrink-0 rounded-md"
            priority
          />
          {!isCollapsed ? (
            <div className="flex min-w-0 flex-col">
              {isSiteGlue ? (
                <span className="text-lg font-bold leading-none tracking-tight">
                  <span style={{ color: chrome.primaryColor }}>Site</span>
                  <span style={{ color: chrome.accentColor }}>Glue</span>
                </span>
              ) : (
                <span className="truncate text-lg font-bold leading-none tracking-tight">
                  {chrome.productName}
                </span>
              )}
              <span className="mt-0.5 truncate text-[10px] leading-none text-muted-foreground">
                {chrome.tagline}
              </span>
            </div>
          ) : null}
        </Link>
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        {!isCollapsed && (
          <div className="flex min-h-0 flex-1 flex-col px-2 pt-2">
            <div className="flex shrink-0 items-center gap-2 px-3 pb-2 text-sm font-medium text-foreground">
              <FolderDot className="h-4 w-4 shrink-0" />
              <span className="truncate">{brand.terminology.myAgentsHeading}</span>
            </div>
            <div className="ml-6 min-h-0 flex-1 overflow-y-auto pb-2 pr-1">
              <div className="space-y-1">
                {projects && projects.length > 0 ? (
                  projects.map((project) => (
                    <Button
                      key={project.id}
                      variant="ghost"
                      className="h-9 w-full justify-start px-3 text-sm hover:bg-blue-50"
                      onClick={() => {
                        router.push(`/projects/${project.id}/inbox`);
                        onNavigate?.();
                      }}
                    >
                      <div className="flex w-full items-center gap-2 overflow-hidden">
                        {project.faviconUrl ? (
                          <img
                            src={project.faviconUrl}
                            alt={project.title || ""}
                            className="h-5 w-5 shrink-0 rounded object-contain"
                            onError={(e) => {
                              e.currentTarget.style.display = "none";
                              const fallback = e.currentTarget
                                .nextElementSibling as HTMLElement;
                              if (fallback) fallback.style.display = "flex";
                            }}
                          />
                        ) : null}
                        <div
                          className={`h-5 w-5 shrink-0 rounded bg-blue-500 items-center justify-center text-xs font-semibold text-white ${project.faviconUrl ? "hidden" : "flex"}`}
                          style={{
                            display: project.faviconUrl ? "none" : "flex",
                          }}
                        >
                          {project.title?.charAt(0)?.toUpperCase() || "P"}
                        </div>
                        <span className="truncate text-left">{project.title}</span>
                      </div>
                    </Button>
                  ))
                ) : (
                  <div className="px-3 py-2 text-xs text-gray-500">
                    {isSidebarAgentsLoading
                      ? `Loading ${brand.terminology.agent.pluralLower}...`
                      : `No ${brand.terminology.agent.pluralLower} found`}
                  </div>
                )}
                {canBuildAndPurchaseAgents(orgRole) ? (
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-2 h-8 w-full justify-start text-xs"
                    onClick={() => {
                      if (isSiteGlue) {
                        clearOnboardSession();
                        resetHireBuilderForNewAgent();
                        router.push(BUILD_NEW_SITEGLUE_AGENT_PATH);
                      } else {
                        router.push(BUILD_NEW_INBOUND_AGENT_PATH);
                      }
                      onNavigate?.();
                    }}
                  >
                    + Build new agent
                  </Button>
                ) : null}
              </div>
            </div>
          </div>
        )}

        <Separator className="shrink-0" />
        <Nav isCollapsed={isCollapsed} links={links} />
      </div>
      <div className="mt-auto flex shrink-0 flex-col gap-2 p-4">
        {!isCollapsed && (
          <Button
            onClick={() => {
              onSupport();
              onNavigate?.();
            }}
            className="mb-2"
            variant="outline"
            size="sm"
          >
            <LifeBuoy className="mr-2 h-4 w-4" />
            Support
          </Button>
        )}
      </div>
    </div>
  );
}
